import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import {TriageAgentDialog} from '@github-ui/issue-triage-agent/TriageAgentDialog'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useNavigate} from '@github-ui/use-navigate'
import {useUser} from '@github-ui/use-user'
import {CopilotIcon, MilestoneIcon, TagIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {BUTTON_LABELS} from '../../constants/buttons'
import type {AppPayload} from '../../types/app-payload'
import type {SearchBarActionsRepositoryFragment$key} from './__generated__/SearchBarActionsRepositoryFragment.graphql'
import styles from './SearchBarActions.module.css'

type SearchBarActionsProps = {
  currentRepository: SearchBarActionsRepositoryFragment$key | null | undefined
}

export function SearchBarActions({currentRepository}: SearchBarActionsProps) {
  const navigate = useNavigate()
  const {currentUser} = useUser()
  const {scoped_repository, current_user_settings} = useAppPayload<AppPayload>()
  const [isTriageAgentDialogOpen, setIsTriageAgentDialogOpen] = useState(false)
  const showTriageAgentButton = scoped_repository && isFeatureEnabled('triage_agent_cta_button')
  const repository_data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchBarActionsRepositoryFragment on Repository {
        isOwnerEnterpriseManaged
        nameWithOwner
      }
    `,
    currentRepository,
  )
  const hideCreateButton =
    scoped_repository?.is_archived || // archived repo
    (currentUser != null && !!currentUser?.is_emu && (!repository_data || !repository_data.isOwnerEnterpriseManaged)) // emu user in a non-owned repo

  return (
    <div className={`${styles.buttons} ${styles.gap8} d-flex flex-wrap`}>
      <Button as="a" href={`/${repository_data?.nameWithOwner}/labels`} leadingVisual={TagIcon}>
        Labels
      </Button>
      <Button as="a" href={`/${repository_data?.nameWithOwner}/milestones`} leadingVisual={MilestoneIcon}>
        Milestones
      </Button>
      {showTriageAgentButton && (
        <Button leadingVisual={CopilotIcon} onClick={() => setIsTriageAgentDialogOpen(true)}>
          Triage agent
        </Button>
      )}
      {!hideCreateButton && (
        <CreateIssueButton
          label={BUTTON_LABELS.newIssue}
          navigate={navigate}
          optionConfig={{
            issueCreateArguments: {
              repository: scoped_repository
                ? {
                    owner: scoped_repository.owner,
                    name: scoped_repository.name,
                  }
                : undefined,
            },
            showRepositoryPicker: scoped_repository === null,
            emojiSkinTonePreference: current_user_settings?.preferred_emoji_skin_tone,
            showFullScreenButton: true,
            canBypassTemplateSelection: true,
            navigate,
          }}
        />
      )}
      {isTriageAgentDialogOpen && scoped_repository && (
        <TriageAgentDialog
          repoOwner={scoped_repository.owner}
          repoName={scoped_repository.name}
          onClose={() => setIsTriageAgentDialogOpen(false)}
        />
      )}
    </div>
  )
}

try{ SearchBarActions.displayName ||= 'SearchBarActions' } catch {}